import { IconCopy } from '@developerz/ui';
import { createSignal, type JSX } from 'solid-js';
import { t } from '../i18n';
import type { ArtifactLink } from '../lib/artifact-actions';
import { Button, buttonClass } from './ui/Button';

// The presigned URL a download or a share just minted. Presenter.
//
// IT IS RENDERED, NOT AUTO-OPENED. The URL only exists after a round trip, and a
// `window.open` that far from the click is what every popup blocker is built to
// stop — a download that silently does not happen is worse than one that costs a
// second, deliberate click on a real anchor. A share needs the link VISIBLE
// anyway (that is the whole act), so one affordance serves both.
//
// IT IS A CREDENTIAL. A presigned URL is a bearer token in a query string, which
// is why all three presign routes answer `no-store`. So it is never routed to,
// never a query key, and never cached: it lives in one signal until dismissed.
// The anchor carries `noopener` (the opened tab must not reach back through
// `window.opener`) and `noreferrer` (the object host is not handed the dashboard
// URL the reader came from).
//
// THE EXPIRY IS STATED, because these links die. A share link pasted into a
// README or a ticket stops working, and nothing else on this screen would say so.

/** Whole minutes, floor 1 — a link with seconds left is still "about a minute". */
export function expiryMinutes(seconds: number): number {
  return Math.max(1, Math.round(seconds / 60));
}

export function ArtifactLinkPanel(props: {
  readonly link: ArtifactLink;
  readonly onDismiss: () => void;
}): JSX.Element {
  const [copied, setCopied] = createSignal(false);
  // Guarded: happy-dom (tests) and non-secure contexts lack navigator.clipboard.
  const copy = (): void =>
    void navigator.clipboard?.writeText(props.link.url).then(
      () => setCopied(true),
      () => setCopied(false),
    );

  return (
    <div class="alert token-secret" role="status">
      <p class="alert__title">
        {t(props.link.kind === 'share' ? 'artifacts.link_shared' : 'artifacts.link_download')}
      </p>
      <p class="alert__desc">
        {t('artifacts.link_expires', {
          minutes: String(expiryMinutes(props.link.expiresInSeconds)),
        })}
      </p>
      <code class="code-block token-secret__value">{props.link.url}</code>
      <div class="token-secret__actions">
        <a
          class={buttonClass({ variant: 'outline', size: 'sm' })}
          href={props.link.url}
          target="_blank"
          rel="noopener noreferrer"
        >
          {t('artifacts.link_open')}
        </a>
        <Button variant="outline" size="sm" onClick={copy}>
          <IconCopy size="0.875rem" />
          {t(copied() ? 'artifacts.link_copied' : 'artifacts.link_copy')}
        </Button>
        <Button variant="ghost" size="sm" onClick={props.onDismiss}>
          {t('artifacts.link_dismiss')}
        </Button>
      </div>
    </div>
  );
}
